import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { AlertTriangle, Phone, MessageCircle, User as UserIcon, CheckCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';


import { BACKEND_URL } from '../../config/api';
const AttentionSection = ({ onRefresh }) => {
  const [attentionFlags, setAttentionFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAttentionFlags();
  }, []);

  const fetchAttentionFlags = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(`${BACKEND_URL}/api/mentor/attention-flags?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttentionFlags(response.data);
    } catch (error) {
      console.error('Error fetching attention flags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshFlags = async () => {
    setRefreshing(true);
    await onRefresh();
    await fetchAttentionFlags();
    setRefreshing(false);
  };

  const handleMarkResolved = async (flagId) => {
    try {
      const token = localStorage.getItem('lms_token');
      await axios.put(
        `${BACKEND_URL}/api/mentor/attention-flags/${flagId}`,
        { status: 'resolved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh list
      fetchAttentionFlags();
    } catch (error) {
      console.error('Error marking flag as resolved:', error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 border-red-400 text-red-800';
      case 'high':
        return 'bg-orange-100 border-orange-400 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 border-yellow-400 text-yellow-800';
      default:
        return 'bg-blue-100 border-blue-400 text-blue-800';
    }
  };

  const getFlagLabel = (flag) => {
    const labels = {
      inactive_7days: 'Inactive >7 days',
      zero_progress: 'Zero Progress',
      failed_quizzes: 'Failed Quizzes',
      fees_pending: 'Fees Pending',
      low_engagement: 'Low Engagement'
    };
    return labels[flag] || flag;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Students Needing Attention</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-600" />
            <div>
              <CardTitle className="text-2xl text-red-900">
                Students Needing Attention
              </CardTitle>
              <p className="text-sm text-red-700 mt-1">
                {attentionFlags.length} student{attentionFlags.length !== 1 ? 's' : ''} require{attentionFlags.length === 1 ? 's' : ''} immediate action
              </p>
            </div>
          </div>
          <button
            onClick={handleRefreshFlags}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Flags
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {attentionFlags.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">All students are doing well!</p>
            <p className="text-sm text-gray-600 mt-2">No attention flags at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto pr-2">
            {attentionFlags.map((flag) => (
              <div
                key={flag.id}
                className={`border-2 rounded-lg p-4 bg-white shadow-md hover:shadow-lg transition-shadow ${
                  flag.severity === 'critical' ? 'ring-2 ring-red-400' : ''
                }`}
              >
                {/* Severity Badge */}
                <div className="flex items-center justify-between mb-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${
                      getSeverityColor(flag.severity)
                    }`}
                  >
                    {flag.severity.toUpperCase()}
                  </span>
                  <AlertTriangle
                    className={`w-5 h-5 ${
                      flag.severity === 'critical'
                        ? 'text-red-600'
                        : flag.severity === 'high'
                        ? 'text-orange-600'
                        : 'text-yellow-600'
                    }`}
                  />
                </div>

                {/* Student Info */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <UserIcon className="w-6 h-6 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Student ID</p>
                    <p className="text-xs text-gray-600 truncate">{flag.student_id}</p>
                  </div>
                </div>

                {/* Flags */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-700 mb-2">Issues:</p>
                  <div className="flex flex-wrap gap-1">
                    {flag.flags.map((f, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full border border-red-300"
                      >
                        {getFlagLabel(f)}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <button className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
                    <Phone className="w-4 h-4" />
                    Call
                  </button>
                  <button className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                    <MessageCircle className="w-4 h-4" />
                    Message
                  </button>
                </div>
                
                <button
                  onClick={() => handleMarkResolved(flag.id)}
                  className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark Resolved
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AttentionSection;
